/**
 * LifecycleTransitionDialog — D-032 §6 transition control.
 * Lets an authorized user move a document through allowed lifecycle transitions.
 */

import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Stack, Typography, Alert,
} from "@mui/material";
import { LIFECYCLE_LABELS, getLifecycle, transitionLifecycle } from "../../utils/documentLifecycleApi";
import LifecycleStatusBadge from "./LifecycleStatusBadge";

export default function LifecycleTransitionDialog({ open, onClose, documentId, onTransitioned }) {
  const [lifecycle, setLifecycle] = useState(null);
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !documentId) return;
    setError(null); setTarget(""); setReason("");
    getLifecycle(documentId)
      .then(setLifecycle)
      .catch((err) => {
        if (err?.response?.status === 404) {
          // Graceful degradation: the document-lifecycle route may not be
          // mounted on this server yet (CR-2026-DC-LIFECYCLE-MOUNT). Show a
          // clear message instead of a broken spinner / raw axios error.
          setError("Lifecycle management isn't enabled on this server yet. Ask your administrator to deploy the document-lifecycle route.");
        } else {
          setError(err?.response?.data?.error || err?.response?.data?.message || err?.message);
        }
      });
  }, [open, documentId]);

  const handleSubmit = async () => {
    if (!target) { setError("Pick a target status"); return; }
    setBusy(true); setError(null);
    try {
      await transitionLifecycle(documentId, target, reason || null);
      onTransitioned?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Transition document #{documentId}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!lifecycle && !error && <Typography>Loading…</Typography>}
        {lifecycle && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ color: "#6c757d" }}>Current:</Typography>
              <LifecycleStatusBadge status={lifecycle.lifecycle_status} />
              {lifecycle.grandfathered && (
                <Typography variant="caption" sx={{ color: "#fd7e14", fontWeight: 600 }}>
                  GRANDFATHERED (pre-deploy row; release-gate preconditions bypassed)
                </Typography>
              )}
            </Stack>

            {lifecycle.allowed_transitions.length === 0 ? (
              <Alert severity="info">No transitions available from {lifecycle.lifecycle_status}.</Alert>
            ) : (
              <>
                <TextField select label="Target status" value={target} onChange={(e) => setTarget(e.target.value)}>
                  {lifecycle.allowed_transitions.map((s) => (
                    <MenuItem key={s} value={s}>{LIFECYCLE_LABELS[s] || s}</MenuItem>
                  ))}
                </TextField>
                <TextField label="Reason (optional)" multiline rows={3}
                  value={reason} onChange={(e) => setReason(e.target.value)}
                  helperText="Captured in audit_log under AISC-7-8-9:DOC-LIFECYCLE-TRANSITION"
                />
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}
          disabled={busy || !target || !lifecycle || lifecycle.allowed_transitions.length === 0}>
          {busy ? "Transitioning…" : "Transition"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
